import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useCurrentSite } from '@hotjar/site';
import { useInProgressSessions } from '@hotjar/webapp/hooks/useInProgressSessions';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { SITE } from '@hotjar/webapp/constants/routes';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { Link, Loader, Text, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useTrackingStatusTrack } from '../hooks/useTrack';
import { TrackingSettingsDetails } from './TrackingSettingsDetails';
import { SiteUrl } from './SiteUrl';

const StyledLink = styled(Link)`
  text-decoration: underline;
  color: ${tokens.color.text_light};

  &:hover,
  &:focus {
    color: ${tokens.color.text_light};
  }
`;

const TickerContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex-direction: row;
  justify-content: space-between;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  margin-bottom: ${tokens.spacer.x2};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.m};
`;

const TickerText = styled.p`
  margin: 0;
`;

const TickerValue = styled.p`
  ${tokens.text('header5')};
  margin-bottom: 0;
  font-weight: 600;
`;

const pulse = keyframes`
  0% {
    color: ${tokens.color.text_success};
  }

  100% {
    color: ${tokens.color.text_default};
  }
`;

const PulseGreen = styled.div`
  color: ${tokens.color.text_default};
  animation: ${pulse} 2s cubic-bezier(0.46, 0.03, 0.52, 0.96);
`;

export function SuccessState({ trackingUIPosition }: { trackingUIPosition?: string }): JSX.Element {
  const { data: currentSite } = useCurrentSite();
  const url = currentSite?.url;
  const { t } = useTranslation();

  const { trackClick } = useTrackingStatusTrack();
  const {
    data: inProgressSessions,
    isIdle,
    isLoading,
  } = useInProgressSessions({
    siteId: currentSite?.id,
  });

  const hasSiteUpdatePermission = usePermission('sites-update');

  return (
    <>
      <PulseGreen key={Math.random()} data-hj-allow>
        <TickerContainer>
          {isIdle || isLoading ? (
            <Loader />
          ) : (
            <>
              <TickerText>Active sessions</TickerText>
              <TickerValue>{abbrNumber(inProgressSessions?.count || 0)}</TickerValue>
            </>
          )}
        </TickerContainer>
      </PulseGreen>
      <div>
        <Text textColor="text_success" as="p">
          {url
            ? t('activation:dashboard.status-card.spaces-capturing-user-sessions')
            : t('activation:dashboard.status-card.capturing-user-sessions-no-url')}
        </Text>
        {url ? <SiteUrl url={url} /> : null}
        {currentSite?.id ? <TrackingSettingsDetails /> : null}
        {hasSiteUpdatePermission ? (
          <StyledLink
            kind="inline"
            href={
              currentSite
                ? getRoute(SITE.SETTINGS.path, {
                    siteId: currentSite.id,
                    '#': 'targeting',
                  })
                : undefined
            }
            onClick={() => trackClick('Edit Session Targeting Sessions', trackingUIPosition)}
          >
            {t('activation:dashboard.status-card.edit-tracking-settings')}
          </StyledLink>
        ) : null}
      </div>
    </>
  );
}
